/**
 * Таблиця позначок.
 *
 * Поведінка перенесена з першої версії: малювання протяжкою, підсвітка доби
 * під курсором, масові перемикачі в шапці, підказки окремим шаром і рядкові
 * кнопки «підтвердити / прибрати підказки». Рядки перемальовуються поодинці,
 * тому місяць на кілька сотень людей лишається плавним.
 */

import {
  awayAt,
  awayKindsOf,
  cellKey,
  dutyAt,
  dutyBlock,
  dutyColumnsOf,
  dutyCountOf,
  dutyCountText,
  dutyKindsOf,
  dutyLoadOf,
  dutyStartsAt,
  layerOfKind,
  mealKindAt,
  markTypeOf,
  paintSpreadOf,
  isCounted,
  isWeekend,
  spreadCells,
  startMealOf,
  todayIso,
  weekdayLong,
  weekdayShort,
  type IsoDate,
  type MarkLayer,
  type MarkOp,
  type MarkTypeDef,
  type MealDef,
  type OrgSettings,
  type Person,
  type PersonMarks,
  type Position,
  type Rank,
  type Unit,
  visibleInMeals,
} from '@kancelaria/shared';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useLock, useLockCheck } from '../store/lock.js';
import { LockIcon } from './ui.js';
import { useRuler } from './useRuler.js';
import { useMarks } from '../store/marks.js';
import type { TableMode, ViewOptions } from '../pages/TablePage.js';
import { toast } from '../store/ui.js';

export interface GridProps {
  people: Person[];
  dates: IsoDate[];
  settings: OrgSettings;
  ranks: Rank[];
  units: Unit[];
  positions: Position[];
  tool: string;
  hintMode: boolean;
  /**
   * `meals` — клітинка на кожен прийом їжі; `duty` — одна клітинка на добу,
   * і наряд лягає двома сусідніми днями (25–26); `away` — теж доба, відпустка
   * чи ЛР на весь день.
   */
  mode: TableMode;
  /** Що показувати: колонки, лінійка днів тижня, підсумки. Меню «Вигляд». */
  view: ViewOptions;
  /** Останні правки з меню «Вигляд»: ключі `personId|date` та `personId|date|meal`. */
  recent?: Set<string>;
  /** Стерті клітинки: той самий ключ → код позначки, що стояла. */
  recentGone?: ReadonlyMap<string, string>;
  /**
   * «Перевірка»: людина → `date|meal` → що не сходиться. Рядок отримує лише
   * свою частину, тож перемальовуються тільки рядки з розбіжностями.
   */
  issues?: ReadonlyMap<string, ReadonlyMap<string, CellIssue>>;
  /** Телефон: тап ставить одну клітинку через click, без протяжки. */
  tapCells?: boolean;
  readOnly: boolean;
  /** Спроба поставити позначку, коли таблиця заблокована, — щоб пояснити чому. */
  onLocked?: () => void;
  onPersonClick?: (p: Person) => void;
  onBulk: (dates: IsoDate[], meals: number[]) => void;
  onHints: (person: Person, action: 'confirm' | 'clear') => void;
}

interface PaintState {
  set: boolean;
  kind: string;
  layer: MarkLayer;
}

/** Ширини колонок підсумку — щоб «прилипати» їх до правого краю під час прокрутки. */
const SUM_W = 44;
/** «Вихідні» — єдина ширша колонка серед підсумкових, бо слово в неї не влазить. */
const WIDE_W = 64;
/** «Всього» показуємо лише в таблиці харчування, тож воно стоїть з краю. */
const TOT_RIGHT = 0;
/** Відступ від правого краю для i-го стовпчика позначки наряду. */
const dutyRight = (dutyKindsCount: number, i: number): number =>
  (dutyKindsCount - 1 - i) * SUM_W + WIDE_W;

/**
 * Позначка «людини немає»: весь день однією позначкою, яка не рахується в
 * компенсацію і не означає їдальню — відпустка чи лікарняний. Компенсація на
 * весь день сюди не належить: у такий день наряд цілком можливий.
 */
/**
 * Що стоїть на добу для пошуку відсутності: таблиця відпусток, а за старі
 * місяці — наряди й харчування, де її ставили раніше. Таблиця нарядів має
 * її бачити, щоб не давати наряд людині, якої немає.
 */
const awayKindsAt = (marks: PersonMarks | undefined, date: IsoDate, mealCount: number): Array<string | null> => {
  const away = awayAt(marks, date, mealCount);
  if (away) return [away];
  return Array.from({ length: mealCount }, (_, mi) => dutyAt(marks, date, mi) ?? marks?.main.get(cellKey(date, mi)) ?? null);
};

function awayKind(types: MarkTypeDef[], dayKinds: Array<string | null>): string | null {
  for (const code of dayKinds) {
    if (!code) continue;
    const type = markTypeOf(types, code);
    if (type && !type.counted && !type.eatsInCanteen) return code;
  }
  return null;
}

/**
 * Усе, що в рядку залежить лише від дати: підпис дня, вихідний, замок.
 *
 * Рахуємо один раз на таблицю й передаємо рядкам готовим. Раніше кожен рядок
 * питав `Intl` назву дня тижня для кожної клітинки — на місяці для сотні людей
 * це десятки тисяч форматувань лише заради підказки при наведенні.
 */
interface DayInfo {
  date: IsoDate;
  /** Число місяця без нуля попереду. */
  n: number;
  weekend: boolean;
  locked: boolean;
  today: boolean;
  short: string;
  long: string;
}

export function Grid(props: GridProps): ReactNode {
  const { people, dates, settings, readOnly } = props;
  const meals = settings.meals;
  const apply = useMarks(s => s.apply);
  const index = useMarks(s => s.index);
  // Підсвітка «у кого нарядів найменше» перебирає всіх людей за всі дні, тож
  // слухаємо не кожну правку, а паузу після неї — і лише коли її ввімкнено.
  const settled = useMarks(s => (props.view.fewestDuties ? s.settled : 0));
  const paint = useRef<PaintState | null>(null);
  const fillRef = useFillRow();
  const today = todayIso();
  // Минулі дні закриті, поки не знято замок: клітинки показуємо іншим курсором,
  // щоб було видно, куди писати не вийде.
  const isLocked = useLockCheck();
  const toggleDayLock = useLock(s => s.toggleDay);
  /** Дні із власним замком — їхній значок видно завжди, не лише під курсором. */
  const dayLocks = useLock(s => s.days);

  /** Дні таблиці з усім, що з них випливає, — раз на таблицю, а не раз на клітинку. */
  const days = useMemo<DayInfo[]>(
    () =>
      dates.map(d => ({
        date: d,
        n: Number(d.slice(8)),
        weekend: isWeekend(d),
        locked: isLocked(d),
        today: d === today,
        short: weekdayShort(d, settings.locale),
        long: weekdayLong(d, settings.locale),
      })),
    [dates, isLocked, today, settings.locale],
  );

  // Позначки нарядів: явні + ті, що обрані в розборі нарядів. Стовпчик мають
  // не всі: кібер наряд рахується половиною звичайного, у його ж колонці.
  // Обидва списки — сталі масиви: інакше кожен перерахунок таблиці підсовував
  // рядкам нові пропси й змушував перемальовуватись усі до одного.
  const dutyKinds = useMemo(() => dutyKindsOf(settings), [settings]);
  const dutyColumns = useMemo(() => dutyColumnsOf(settings), [settings]);
  const awayCodes = useMemo(() => awayKindsOf(settings.markTypes), [settings]);
  // Стовпчики підсумку праворуч: у нарядах — наряди, у відпустках — відсутність.
  const sumColumns = props.mode === 'away' ? awayCodes : props.mode === 'duty' ? dutyColumns : [];

  const { wrapRef, colRef, rowRef, tableRef, headRef, footRef, moveRuler } = useRuler(props.view.crosshair, people.length);

  useEffect(() => {
    const stop = (): void => {
      paint.current = null;
    };
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    return () => {
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
  }, []);


  const opsFor = useCallback(
    (personId: string, date: IsoDate, meal: number, state: PaintState): MarkOp[] => {
      const type = markTypeOf(settings.markTypes, state.kind);
      // Знімаємо позначку, що займає добу, — прибираємо весь її блок.
      const spread = state.set ? (type?.spread ?? 1) : 1;
      const cells = spreadCells(date, meal, spread, meals.length);
      return cells
        .filter(c => dates.includes(c.date))
        .map(c => ({
          personId,
          date: c.date,
          meal: c.meal,
          kind: state.set ? state.kind : null,
          layer: state.layer,
        }));
    },
    [settings.markTypes, meals.length, dates],
  );

  /**
   * Режим нарядів: клітинка — це доба. Наряд починається з останнього прийому
   * їжі й переходить на наступний день, тож у таблиці він видно як 25–26.
   */
  const touchDuty = useCallback(
    (personId: string, date: IsoDate): void => {
      if (readOnly) {
        props.onLocked?.();
        return;
      }
      const kinds = dutyKinds;
      const marks = index.person(personId);

      // Таблиця відпусток: позначка стає на всю добу одним кліком і так само
      // знімається. Наряд того дня — суперечність: людини немає, тож наряд
      // знімаємо, інакше він лишився б у таблиці нарядів.
      if (props.mode === 'away') {
        if (!awayCodes.includes(props.tool)) return;
        const set = awayAt(marks, date, meals.length) !== props.tool;
        const ops: MarkOp[] = [];
        const dayDuty = kinds.find(k => meals.some((_, mi) => dutyAt(marks, date, mi) === k));
        if (set && dayDuty) {
          const block = dutyBlock(marks, date, dayDuty, meals.length);
          ops.push(...block.filter(c => dates.includes(c.date)).map(c => ({ ...c, personId, kind: null })));
        }
        ops.push(
          ...meals.map((_, mi) => ({ personId, date, meal: mi, kind: set ? props.tool : null, layer: 'away' as const })),
        );
        apply(ops);
        return;
      }

      const dayKinds = meals.map((_, mi) => dutyAt(marks, date, mi));
      const existing = kinds.find(k => dayKinds.includes(k));
      const away = awayKind(settings.markTypes, awayKindsAt(marks, date, meals.length));

      // Палітра режиму вже фільтрує інструменти, але приймаємо лише наряд.
      const kind = kinds.includes(props.tool) ? props.tool : kinds[0];
      if (!kind) return;

      // Людини цього дня немає (відпустка, лікарняний) — наряд не ставимо.
      if (!existing && away) {
        const type = markTypeOf(settings.markTypes, away);
        toast(`${type?.label ?? 'Відсутній'} — наряд сюди не ставимо`, 'err');
        return;
      }
      if (existing) {
        const block = dutyBlock(marks, date, existing, meals.length);
        apply(block.filter(c => dates.includes(c.date)).map(c => ({ ...c, personId, kind: null })));
        // Клік іншою позначкою по зайнятій добі міняє наряд, а не просто стирає.
        if (existing === kind) return;
      }
      const type = markTypeOf(settings.markTypes, kind);
      // Звідки починається доба — з налаштувань позначки: типово вечеря.
      const cells = spreadCells(
        date,
        startMealOf(type, meals.length),
        paintSpreadOf(type, meals.length),
        meals.length,
      );
      const layer = layerOfKind(settings.markTypes, kind);
      apply(cells.filter(c => dates.includes(c.date)).map(c => ({ ...c, personId, kind, layer })));
    },
    [readOnly, props.onLocked, props.tool, props.mode, settings, meals, index, apply, dates, dutyKinds, awayCodes],
  );

  const touch = useCallback(
    (personId: string, date: IsoDate, meal: number, start: boolean): void => {
      if (readOnly) {
        if (start) props.onLocked?.();
        return;
      }
      // Наряди ставляться в таблиці нарядів, а сюди — кнопкою перенесення.
      if (dutyKinds.includes(props.tool)) return;
      const layer: 'main' | 'hint' = props.hintMode ? 'hint' : 'main';
      if (start) {
        const current = index.get(personId, date, meal, layer);
        paint.current = { set: current !== props.tool, kind: props.tool, layer };
      }
      const state = paint.current;
      if (!state) return;
      apply(opsFor(personId, date, meal, state));
    },
    [readOnly, props.onLocked, props.hintMode, props.tool, index, apply, opsFor, dutyKinds],
  );

  /**
   * Кого підсвітити: у кого нарядів за показані дні найменше — саме їм черга.
   * Коли в усіх порівну, підсвічувати нема кого: рівність і так видно.
   * Хук стоїть до порожнього return: інакше пошук без збігів знімає useMemo
   * і React валить увесь екран.
   */
  const fewest = useMemo(() => {
    void settled;
    if (!props.view.fewestDuties || people.length < 2) return new Set<string>();
    const load = people.map(p =>
      dutyLoadOf(index.person(p.id), dates, dutyKinds, settings.markTypes, meals.length),
    );
    const min = Math.min(...load);
    if (min === Math.max(...load)) return new Set<string>();
    return new Set(people.filter((_, i) => load[i] === min).map(p => p.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.view.fewestDuties,
    people,
    dates,
    index,
    settled,
    settings.markTypes,
    meals.length,
    dutyKinds,
  ]);

  // Наряди й відпустки — клітинка на добу; харчування — на кожен прийом їжі.
  const duty = props.mode !== 'meals';

  return (
    <div className="grid-wrap" ref={wrapRef} onMouseLeave={() => moveRuler(null, null)}>
      {/* Дві накладки-лінійки: рухаються за курсором без перерахунку стилів. */}
      <div className="ruler-col" ref={colRef} />
      <div className="ruler-row" ref={rowRef} />
      {/* Липкі колонки мають знати, де вони стоять: набір колонок міняється
          в меню «Вигляд», тож зсуви рахуємо тут, а не в CSS. */}
      <table
        ref={tableRef}
        className={`grid${duty ? ' duty' : ''}${props.view.crosshair ? ' ruler' : ''}${
          props.view.dayLines ? ' daylines' : ''
        }`}
        style={
          {
            '--l-rank': `${props.view.numbers ? 34 : 0}px`,
            '--l-name': `${(props.view.numbers ? 34 : 0) + (props.view.rank ? 84 : 0)}px`,
          } as CSSProperties
        }
      >
        <thead ref={headRef}>
          <tr>
            {props.view.numbers ? (
              <th className="sticky c-num" rowSpan={duty ? 1 : 2}>
                №
              </th>
            ) : null}
            {props.view.rank ? (
              <th className="sticky c-rank" rowSpan={duty ? 1 : 2}>
                Звання
              </th>
            ) : null}
            <th className="sticky c-name" rowSpan={duty ? 1 : 2}>
              Прізвище, ім’я та по батькові
            </th>
            {props.view.unit ? (
              <th className="sticky c-grp" rowSpan={duty ? 1 : 2}>
                Група
              </th>
            ) : null}
            {days.map(day => (
              <th
                key={day.date}
                className={`day dsep${day.weekend ? ' we' : ''}${day.today ? ' today' : ''}${
                  day.locked ? ' locked' : ''
                }`}
                colSpan={duty ? 1 : meals.length}
                data-day={day.date}
                title={
                  readOnly
                    ? `${day.n} число`
                    : day.locked
                      ? `${day.n} число закрите. Натисніть, щоб відкрити цей день`
                      : `Натисніть, щоб закрити ${day.n} число`
                }
                onClick={() => (readOnly ? props.onLocked?.() : toggleDayLock(day.date))}
              >
                <div className="n">{day.n}</div>
                <div className="w">{day.short}</div>
                {day.date in dayLocks ? (
                  <span className={`day-lock${day.locked ? ' on' : ''}`}>
                    <LockIcon open={!day.locked} />
                  </span>
                ) : null}
              </th>
            ))}
            {!duty && props.view.totals ? (
              <th
                className="c-tot"
                rowSpan={2}
                style={{ right: TOT_RIGHT }}
                title="Скільки клітинок рахується в компенсацію"
              >
                Всього
              </th>
            ) : null}
            {(duty && props.view.totals ? sumColumns : []).map((k, i) => (
              <th
                key={k}
                className="c-sum grp"
                rowSpan={duty ? 1 : 2}
                style={{ right: dutyRight(sumColumns.length, i) }}
                title={settings.markTypes.find(t => t.code === k)?.label ?? k}
              >
                {settings.markTypes.find(t => t.code === k)?.letter ?? k}
              </th>
            ))}
            {duty && props.view.totals ? (
              <th
                className="c-sum grp wide"
                rowSpan={1}
                style={{ right: 0 }}
                title={
                  props.mode === 'away'
                    ? 'Скільки днів людини немає за показаний період'
                    : 'Скільки разів наряд починався в суботу чи неділю'
                }
              >
                {props.mode === 'away' ? 'Днів' : 'Вихідні'}
              </th>
            ) : null}
          </tr>
          <tr hidden={duty}>
            {days.map(day =>
              meals.map((m, mi) => (
                <th
                  key={day.date + m.code}
                  className={`meal${mi === meals.length - 1 ? ' dsep' : ''}${day.weekend ? ' we' : ''}${
                    day.today ? ' today' : ''
                  }`}
                  data-d={day.date}
                  title={`${m.name} ${day.n} — позначити всім`}
                  onClick={() => (readOnly ? props.onLocked?.() : props.onBulk([day.date], [mi]))}
                >
                  {m.short}
                </th>
              )),
            )}
          </tr>
        </thead>
        <tbody>
          {people.map((p, i) => (
            <Row
              key={p.id}
              person={p}
              n={i + 1}
              days={days}
              settings={settings}
              ranks={props.ranks}
              units={props.units}
              positions={props.positions}
              onTouch={touch}
              onTouchDuty={touchDuty}
              onHover={moveRuler}
              onName={props.onPersonClick}
              onHints={props.onHints}
              dutyKinds={dutyKinds}
              dutyColumns={sumColumns}
              fewest={fewest.has(p.id)}
              recent={props.recent}
              recentGone={props.recentGone}
              issues={props.issues?.get(p.id)}
              tapCells={props.tapCells}
              mode={props.mode}
              view={props.view}
            />
          ))}
          <FillRow
            fillRef={fillRef}
            view={props.view}
            days={days}
            perDay={duty ? 1 : meals.length}
            empty={!people.length}
            tail={
              <>
                {!duty && props.view.totals ? <td className="c-tot" style={{ right: TOT_RIGHT }} /> : null}
                {(duty && props.view.totals ? sumColumns : []).map((k, i) => (
                  <td key={k} className="c-sum grp" style={{ right: dutyRight(sumColumns.length, i) }} />
                ))}
                {duty && props.view.totals ? <td className="c-sum grp wide" style={{ right: 0 }} /> : null}
              </>
            }
          />
        </tbody>
        {props.view.footer ? (
          <Footer
            ref={footRef}
            people={people}
            dates={dates}
            settings={settings}
            mode={props.mode}
            view={props.view}
          />
        ) : null}
      </table>
    </div>
  );
}

/**
 * Порожній хвіст таблиці: сітка тягнеться до низу вікна, навіть коли людей
 * мало. Пошук прибирає рядки, а не таблицю — шапка, колонки й підсумки стоять
 * на своїх місцях, підсумки лишаються внизу.
 *
 * Висоту рядка-заповнювача рахуємо самі: зайву висоту таблиці браузер ділить
 * між рядками як заманеться, і справжні рядки б розтягувались.
 */
export function useFillRow(): React.RefObject<HTMLTableRowElement | null> {
  const fillRef = useRef<HTMLTableRowElement | null>(null);
  useLayoutEffect(() => {
    const fill = fillRef.current;
    const table = fill?.closest('table');
    const wrap = table?.parentElement;
    if (!fill || !table || !wrap) return;
    const fit = (): void => {
      const rest = table.offsetHeight - fill.offsetHeight;
      const h = `${Math.max(0, wrap.clientHeight - rest)}px`;
      // Та сама висота — нічого не чіпаємо, інакше спостерігач ганяв би себе.
      if (fill.style.height !== h) fill.style.height = h;
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(wrap);
    ro.observe(table);
    return () => ro.disconnect();
  }, []);
  return fillRef;
}

/**
 * Рядок-заповнювач: ті самі колонки, що й у людей, без вмісту. Лінії рядків
 * малює фон комірок, тож один рядок виглядає як порожні рядки до низу.
 */
export function FillRow(props: {
  fillRef: React.Ref<HTMLTableRowElement>;
  view: ViewOptions;
  days: { date: string; weekend: boolean }[];
  /** Скільки клітинок на добу: 1 у нарядах, по прийому їжі в харчуванні. */
  perDay: number;
  /** Підсумкові колонки праворуч — з тими ж зсувами, що й у рядках людей. */
  tail: ReactNode;
  empty: boolean;
}): ReactNode {
  return (
    <tr className="grid-fill" ref={props.fillRef} aria-hidden>
      {props.view.numbers ? <td className="sticky c-num" /> : null}
      {props.view.rank ? <td className="sticky c-rank" /> : null}
      <td className="sticky c-name">
        {props.empty ? <span className="grid-fill-note">Нікого не знайдено</span> : null}
      </td>
      {props.view.unit ? <td className="sticky c-grp" /> : null}
      {props.days.map(day =>
        Array.from({ length: props.perDay }, (_, i) => (
          <td
            key={day.date + i}
            className={`cell${i === props.perDay - 1 ? ' dsep' : ''}${day.weekend ? ' we' : ''}`}
          />
        )),
      )}
      {props.tail}
    </tr>
  );
}

/* -------------------------------------------------------------------- рядок */

interface RowProps {
  person: Person;
  n: number;
  /** Дні таблиці з готовими підписами, вихідними й замком. */
  days: DayInfo[];
  settings: OrgSettings;
  ranks: Rank[];
  units: Unit[];
  positions: Position[];
  onTouch: (personId: string, date: IsoDate, meal: number, start: boolean) => void;
  onTouchDuty: (personId: string, date: IsoDate) => void;
  mode: TableMode;
  onHover: (cell: HTMLElement | null, date: IsoDate | null) => void;
  onName?: (p: Person) => void;
  onHints: (person: Person, action: 'confirm' | 'clear') => void;
  /** Позначки нарядів («н», «нк», «чп»…) — для стовпчиків підрахунку. */
  dutyKinds: string[];
  /**
   * Стовпчики підсумку: у нарядах — ті наряди, що мають власний стовпчик
   * (решта додається до чужого), у відпустках — кожна позначка відсутності.
   */
  dutyColumns: string[];
  /** У цієї людини нарядів найменше — рядок підсвічується. */
  fewest: boolean;
  recent?: Set<string>;
  recentGone?: ReadonlyMap<string, string>;
  issues?: ReadonlyMap<string, CellIssue>;
  tapCells?: boolean;
  view: ViewOptions;
}

/** Розбіжність у клітинці: з рапортом, з розходом або з обома. */
export interface CellIssue {
  report: boolean;
  expense: boolean;
  title: string;
}

const Row = memo(function Row(props: RowProps): ReactNode {
  const { person, days, settings } = props;
  const meals = settings.meals;
  const types = settings.markTypes;
  // Підписка саме на цей рядок: сусідні правки його не перемальовують.
  useMarks(s => s.rowVersions.get(person.id) ?? 0);
  const index = useMarks(s => s.index);
  const marks = index.person(person.id);

  let total = 0;
  const byDuty = new Map<string, number>();
  let weekendStart = 0;
  const cells: ReactNode[] = [];

  const duty = props.mode !== 'meals';

  for (const day of days) {
    const d = day.date;
    const weekend = day.weekend;
    const locked = day.locked;

    if (props.mode === 'away') {
      // Відпустка на добу: одна клітинка, підсумок — днями по кожній позначці.
      const kind = awayAt(marks, d, meals.length);
      const type = kind ? markTypeOf(types, kind) : undefined;
      if (kind) {
        byDuty.set(kind, (byDuty.get(kind) ?? 0) + 1);
        weekendStart++;
      }
      const style: CSSProperties = {};
      if (type) {
        const off = locked ? '-off' : '';
        (style as Record<string, string>)['--mk-bg'] = `var(--mk-${type.code}-bg${off})`;
        (style as Record<string, string>)['--mk-fg'] = `var(--mk-${type.code}-fg${off})`;
      }
      cells.push(
        <td
          key={d}
          className={['cell', 'dsep', weekend ? 'we' : '', type ? 'marked' : '', locked ? 'locked' : '']
            .filter(Boolean)
            .join(' ')}
          style={style}
          data-d={d}
          data-letter={type?.letter ?? ''}
          title={`${day.n} ${day.long}${type ? ` — ${type.label}` : ''}`}
          onPointerEnter={e => props.onHover(e.currentTarget, d)}
          onClick={() => props.onTouchDuty(person.id, d)}
        />,
      );
      continue;
    }

    if (duty) {
      // Одна клітинка на добу: спершу наряд, а якщо його немає — позначка, що
      // займає весь день (відпустка, лікарняний): у такий день наряд не дають.
      const dayKinds = meals.map((_, mi) => dutyAt(marks, d, mi));
      const away = awayKind(types, awayKindsAt(marks, d, meals.length));
      const kind = props.dutyKinds.find(k => dayKinds.includes(k)) ?? away;
      const isDuty = !!kind && props.dutyKinds.includes(kind);
      const type = kind ? markTypeOf(types, kind) : undefined;
      const goneKind = !type
        ? (props.recentGone?.get(`${person.id}|${d}`) ??
          meals.map((_, mi) => props.recentGone?.get(`${person.id}|${d}|${mi}`)).find(Boolean))
        : undefined;
      const goneType = goneKind ? markTypeOf(types, goneKind) : undefined;
      // Доба чергування рахується один раз — за днем, у якому вона почалася.
      const starts =
        isDuty && !!kind && meals.some((_, mi) => dutyStartsAt(marks, d, mi, kind, meals.length));
      if (kind && starts) {
        const count = dutyCountOf(types, kind);
        byDuty.set(count.code, (byDuty.get(count.code) ?? 0) + count.weight);
        if (weekend) weekendStart++;
      }
      const style: CSSProperties = {};
      if (type) {
        const off = locked ? '-off' : '';
        (style as Record<string, string>)['--mk-bg'] = `var(--mk-${type.code}-bg${off})`;
        (style as Record<string, string>)['--mk-fg'] = `var(--mk-${type.code}-fg${off})`;
      }
      cells.push(
        <td
          key={d}
          className={[
            'cell',
            'dsep',
            weekend ? 'we' : '',
            type ? 'marked' : '',
            locked ? 'locked' : '',
            props.recent?.has(`${person.id}|${d}`) ? 'recent' : '',
            goneKind ? 'recent-gone' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={style}
          data-d={d}
          data-letter={type?.letter ?? ''}
          data-recent-letter={goneType?.letter ?? goneKind ?? ''}
          title={`${day.n} ${day.long}${type ? ` — ${type.label}` : goneType ? ` — знято: ${goneType.label}` : ''}`}
          onPointerEnter={e => props.onHover(e.currentTarget, d)}
          onClick={() => props.onTouchDuty(person.id, d)}
        />,
      );
      // Суму компенсації рахуємо однаково в обох режимах.
      for (let mi = 0; mi < meals.length; mi++) {
        if (isCounted(types, mealKindAt(marks, d, mi))) total++;
      }
      continue;
    }

    for (let mi = 0; mi < meals.length; mi++) {
      const key = cellKey(d, mi);
      // Лише харчування: наряди потрапляють сюди кнопкою «Перенести наряди».
      const kind = mealKindAt(marks, d, mi);
      const hint = marks?.hint.get(key) ?? null;
      const found = kind ? markTypeOf(types, kind) : undefined;
      // Явне правило перенесення має перевагу над старим dutyOnly.
      // Вимкнення перенесення не приховує вже записані клітинки.
      const type = found && visibleInMeals(found) ? found : undefined;
      const hintType = hint ? markTypeOf(types, hint) : undefined;
      const goneKind = !found ? props.recentGone?.get(`${person.id}|${d}|${mi}`) : undefined;
      const goneType = goneKind ? markTypeOf(types, goneKind) : undefined;
      const issue = props.issues?.get(`${d}|${mi}`);
      if (isCounted(types, kind)) total++;
      // Рахуємо саме клітинку, з якої доба почалася, інакше три клітинки
      // одного наряду порахуються тричі.
      const dutyKind = dutyAt(marks, d, mi);
      if (
        dutyKind &&
        props.dutyKinds.includes(dutyKind) &&
        dutyStartsAt(marks, d, mi, dutyKind, meals.length)
      ) {
        const count = dutyCountOf(types, dutyKind);
        byDuty.set(count.code, (byDuty.get(count.code) ?? 0) + count.weight);
        if (weekend) weekendStart++;
      }

      const style: CSSProperties = {};
      const off = locked ? '-off' : '';
      if (type) {
        (style as Record<string, string>)['--mk-bg'] = `var(--mk-${type.code}-bg${off})`;
        (style as Record<string, string>)['--mk-fg'] = `var(--mk-${type.code}-fg${off})`;
      }
      if (hintType) {
        (style as Record<string, string>)['--hint-bg'] = `var(--mk-${hintType.code}-bg)`;
        (style as Record<string, string>)['--hint-fg'] = `var(--mk-${hintType.code}-fg)`;
      }

      const cls = [
        'cell',
        mi === meals.length - 1 ? 'dsep' : '',
        weekend ? 'we' : '',
        locked ? 'locked' : '',
        type ? 'marked' : '',
        hintType ? 'hint' : '',
        hintType && type && hintType.code !== type.code ? 'conflict' : '',
        props.recent?.has(`${person.id}|${d}|${mi}`) ? 'recent' : '',
        goneKind ? 'recent-gone' : '',
        issue?.report ? 'issue-report' : '',
        issue?.expense ? 'issue-expense' : '',
      ]
        .filter(Boolean)
        .join(' ');

      cells.push(
        <td
          key={key}
          className={cls}
          style={style}
          data-d={d}
          data-letter={type?.letter ?? ''}
          data-hint-letter={hintType?.letter ?? ''}
          data-meal={mi}
          data-recent-letter={goneType?.letter ?? goneKind ?? ''}
          title={`${day.n} ${day.long} — ${meals[mi]?.name ?? ''}${goneType ? ` — знято: ${goneType.label}` : ''}${issue ? `\n${issue.title}` : ''}`}
          onPointerDown={e => {
            // На телефоні — лише тап у onClick; палець лишаємо прокрутці.
            if (props.tapCells || e.pointerType === 'touch') return;
            e.preventDefault();
            props.onTouch(person.id, d, mi, true);
          }}
          onPointerEnter={e => {
            props.onHover(e.currentTarget, d);
            if (props.tapCells) return;
            if (e.buttons === 1) props.onTouch(person.id, d, mi, false);
          }}
          onClick={() => {
            if (!props.tapCells) return;
            props.onTouch(person.id, d, mi, true);
            props.onTouch(person.id, d, mi, false);
          }}
        />,
      );
    }
  }

  const rank = props.ranks.find(r => r.code === person.rankCode);
  const unit = props.units.find(u => u.id === person.unitId);
  const position = props.positions.find(x => x.code === person.positionCode && x.short);
  const pattern = settings.mealPatterns.find(mp => mp.id === person.patternId);

  return (
    <tr className={props.fewest ? 'fewest' : undefined} data-person={person.id}>
      {props.view.numbers ? <td className="sticky c-num">{props.n}</td> : null}
      {props.view.rank ? (
        <td className="sticky c-rank">{rank?.short || rank?.name || ''}</td>
      ) : null}
      <td
        className="sticky c-name"
        title={[rank?.name, person.lastName, unit?.name, person.taxCode]
          .filter(Boolean)
          .join(' • ')}
      >
        <span className="nm" onClick={() => props.onName?.(person)}>
          {person.lastName} {person.firstName ? person.firstName.charAt(0) + '.' : ''}
          {person.patronymic ? person.patronymic.charAt(0) + '.' : ''}
        </span>
        <span className="dots">
          <i
            className={`dot-flag home${pattern && !pattern.isDefault ? ' on' : ''}`}
            title={pattern?.name ?? 'категорія'}
          />
          <i
            className={`dot-flag cmd${position ? ' on' : ''}`}
            title={position?.name ?? 'посада'}
          />
        </span>
        <span className="rowbtns">
          <b
            title="Підтвердити підказки — зробити їх звичайними позначками"
            onClick={() => props.onHints(person, 'confirm')}
          >
            ✓
          </b>
          <b
            title="Прибрати підказки"
            className="danger"
            onClick={() => props.onHints(person, 'clear')}
          >
            ◌
          </b>
        </span>
      </td>
      {props.view.unit ? <td className="sticky c-grp">{unit?.name ?? ''}</td> : null}
      {cells}
      {!duty && props.view.totals ? (
        <td className="c-tot" style={{ right: TOT_RIGHT }}>
          {total || ''}
        </td>
      ) : null}
      {(duty && props.view.totals ? props.dutyColumns : []).map((k, i) => (
        <td key={k} className="c-sum grp" style={{ right: dutyRight(props.dutyColumns.length, i) }}>
          {dutyCountText(byDuty.get(k) ?? 0)}
        </td>
      ))}
      {duty && props.view.totals ? (
        <td className="c-sum grp wide" style={{ right: 0 }}>
          {weekendStart || ''}
        </td>
      ) : null}
    </tr>
  );
});

/* ------------------------------------------------------------------ підсумки */

const Footer = memo(function Footer(props: {
  people: Person[];
  dates: IsoDate[];
  settings: OrgSettings;
  mode: TableMode;
  view: ViewOptions;
  ref?: React.Ref<HTMLTableSectionElement>;
}): ReactNode {
  const index = useMarks(s => s.index);
  // Підсумки перебирають усіх людей за всі дні, тож перераховуємо їх не на
  // кожну клітинку протяжки, а коли правки вщухли: смуга внизу оновлюється
  // за мить після руки й нічого не гальмує.
  const settled = useMarks(s => s.settled);
  void settled;
  const meals = props.settings.meals;
  const types = props.settings.markTypes;
  const dutyKinds = dutyKindsOf(props.settings);
  const dutyColumns = dutyColumnsOf(props.settings);

  // Таблиця відпусток: скільки людей відсутні кожної доби й скільки днів за кожною позначкою.
  if (props.mode === 'away') {
    const awayColumns = awayKindsOf(types);
    const perDay: number[] = [];
    const byKind = new Map<string, number>();
    let days = 0;
    for (const d of props.dates) {
      let absent = 0;
      for (const p of props.people) {
        const kind = awayAt(index.person(p.id), d, meals.length);
        if (!kind) continue;
        absent++;
        days++;
        byKind.set(kind, (byKind.get(kind) ?? 0) + 1);
      }
      perDay.push(absent);
    }
    return (
      <tfoot ref={props.ref}>
        <tr>
          {props.view.numbers ? <td className="sticky c-num" /> : null}
          {props.view.rank ? <td className="sticky c-rank" /> : null}
          <td className="sticky c-name">Відсутні</td>
          {props.view.unit ? <td className="sticky c-grp" /> : null}
          {props.dates.map((d, i) => (
            <td key={d} className="dsep" data-d={d}>
              {perDay[i] || ''}
            </td>
          ))}
          {(props.view.totals ? awayColumns : []).map((k, i) => (
            <td key={k} className="c-sum grp" style={{ right: dutyRight(awayColumns.length, i) }}>
              {byKind.get(k) || ''}
            </td>
          ))}
          {props.view.totals ? (
            <td className="c-sum grp wide" style={{ right: 0 }}>
              {days || ''}
            </td>
          ) : null}
        </tr>
      </tfoot>
    );
  }

  // Таблиця нарядів: скільки людей у наряді кожної доби, і скільки їх почалося.
  if (props.mode === 'duty') {
    const perDay: number[] = [];
    const byKind = new Map<string, number>();
    let weekendStarts = 0;
    for (const d of props.dates) {
      let onDuty = 0;
      for (const p of props.people) {
        const marks = index.person(p.id);
        const kind = dutyKinds.find(k => meals.some((_, mi) => dutyAt(marks, d, mi) === k));
        if (!kind) continue;
        onDuty++;
        // Добу рахуємо за днем, у якому вона почалася, — інакше подвоїться.
        if (meals.some((_, mi) => dutyStartsAt(marks, d, mi, kind, meals.length))) {
          const count = dutyCountOf(types, kind);
          byKind.set(count.code, (byKind.get(count.code) ?? 0) + count.weight);
          if (isWeekend(d)) weekendStarts++;
        }
      }
      perDay.push(onDuty);
    }

    return (
      <tfoot ref={props.ref}>
        <tr>
          {props.view.numbers ? <td className="sticky c-num" /> : null}
          {props.view.rank ? <td className="sticky c-rank" /> : null}
          <td className="sticky c-name">У наряді</td>
          {props.view.unit ? <td className="sticky c-grp" /> : null}
          {props.dates.map((d, i) => (
            <td key={d} className="dsep" data-d={d}>
              {perDay[i] || ''}
            </td>
          ))}
          {(props.view.totals ? dutyColumns : []).map((k, i) => (
            <td key={k} className="c-sum grp" style={{ right: dutyRight(dutyColumns.length, i) }}>
              {dutyCountText(byKind.get(k) ?? 0)}
            </td>
          ))}
          {props.view.totals ? (
            <td className="c-sum grp wide" style={{ right: 0 }}>
              {weekendStarts || ''}
            </td>
          ) : null}
        </tr>
      </tfoot>
    );
  }

  const compensated: number[] = [];
  const eating: number[] = [];
  for (const d of props.dates) {
    for (let mi = 0; mi < meals.length; mi++) {
      let comp = 0;
      let eat = 0;
      for (const p of props.people) {
        const kind = mealKindAt(index.person(p.id), d, mi);
        if (isCounted(types, kind)) comp++;
        // Немає позначки — людина за замовчуванням їсть у їдальні;
        // позначка (компенсація, відпустка…) з eatsInCanteen: false виключає її.
        const type = kind ? markTypeOf(types, kind) : undefined;
        if (!type || type.eatsInCanteen) eat++;
      }
      compensated.push(comp);
      eating.push(eat);
    }
  }

  const cellsOf = (values: number[], showZero: boolean): ReactNode[] => {
    let i = 0;
    return props.dates.flatMap(d =>
      meals.map((m, mi) => {
        const v = values[i++]!;
        return (
          <td key={d + m.code} className={mi === meals.length - 1 ? 'dsep' : ''} data-d={d}>
            {v || (showZero ? 0 : '')}
          </td>
        );
      }),
    );
  };

  return (
    <tfoot ref={props.ref}>
      <tr>
        {props.view.numbers ? <td className="sticky c-num" /> : null}
        {props.view.rank ? <td className="sticky c-rank" /> : null}
        <td className="sticky c-name">Компенсація</td>
        {props.view.unit ? <td className="sticky c-grp" /> : null}
        {cellsOf(compensated, false)}
        {props.view.totals ? (
          <td className="c-tot" style={{ right: TOT_RIGHT }}>
            {compensated.reduce((a, b) => a + b, 0) || ''}
          </td>
        ) : null}
      </tr>
      <tr className="eat">
        {props.view.numbers ? <td className="sticky c-num" /> : null}
        {props.view.rank ? <td className="sticky c-rank" /> : null}
        <td className="sticky c-name">На харчуванні</td>
        {props.view.unit ? <td className="sticky c-grp" /> : null}
        {cellsOf(eating, true)}
        {props.view.totals ? <td className="c-tot" style={{ right: TOT_RIGHT }} /> : null}
      </tr>
    </tfoot>
  );
});

export type { MarkTypeDef, MealDef };
